/*
 * Onboarding 002 — API calls.
 *
 * Everything goes through the Shopify App Proxy path (default /apps/reformly-api), which
 * means: same origin so no CORS, the API host never appears in client code, and Shopify
 * signs every request with a secret only it and the server know. That signature replaces
 * the browser-computed HMAC the Next version used — which could never be more than a speed
 * bump, since any secret the browser can compute with is a secret the browser has given away.
 */
window.ReformlyQuiz = window.ReformlyQuiz || {};

(function (NS) {
  'use strict';

  function base() { return (NS.config && NS.config.api) || '/apps/reformly-api'; }

  function request(path, opts) {
    opts = opts || {};
    return fetch(base() + path, {
      method: opts.method || 'GET',
      /*
       * `Accept: application/json` is not decoration. The App Proxy forwards the visitor's
       * browser headers, so a tunnel or CDN in front of the API can mistake an API call for
       * someone browsing — ngrok answers GETs that accept text/html with its own interstitial
       * page, at status 200, which parses as "not our JSON" rather than as an error.
       */
      headers: Object.assign(
        { 'Content-Type': 'application/json', Accept: 'application/json' },
        opts.headers || {}
      ),
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    }).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
        if (!res.ok) {
          var msg = (data && (data.message || data.error)) || ('Request failed (' + res.status + ')');
          throw new Error(Array.isArray(msg) ? msg.join(', ') : msg);
        }
        return data;
      });
    });
  }

  /**
   * Creates the account and writes the whole quiz in one call.
   *
   * Resolves to { existingAccount: true } when the email is already registered — the quiz
   * is anonymous up to this point, so the address is unproven and the server must not issue
   * a session for it. The caller sends the user back to the email screen.
   */
  function quizSignup(payload) {
    return request('/auth/quiz-signup', { method: 'POST', body: payload });
  }

  /**
   * Public availability check — the user has no token yet, so this cannot be authenticated.
   *
   * POST, not GET: the App Proxy forwards the visitor's browser headers, so a GET can be
   * mistaken for someone browsing by anything sitting in front of the API. An ngrok tunnel
   * answers those with an HTML interstitial at status 200, which parses as "not our JSON"
   * rather than as a failure.
   */
  function checkUsername(username, signal) {
    return request('/auth/check-username', { method: 'POST', body: { username: username }, signal: signal });
  }

  /*
   * Referrer name for the landing screen. Public endpoint, no auth.
   *
   * ONE endpoint resolves BOTH code namespaces — referrals.service.ts:25 notes that
   * friend codes (REF-...) live in referralCode and affiliate codes in affiliateCode,
   * and getReferrerByCode tries both. So ?referralCode= and ?ref= are equally valid
   * here, and reading either is not conflating two features.
   *
   * Resolves to null on any failure — a bad code, a 404, a proxy hiccup. The caller
   * treats null as "no landing" and drops straight into the quiz rather than blocking.
   */
  function getReferrer(code) {
    return request('/referrals/referrer?code=' + encodeURIComponent(code))
      .then(function (r) {
        /* Console log, not an event: the failure here is silent by design — the
           visitor just lands in the quiz — so without this a bad code, an
           unroutable path and a null username all look identical while testing.
           Logs the parsed body, so a Nest error object is as visible as a hit. */
        try { console.log('[quiz002] referrer lookup ok', { code: code, response: r }); } catch (e) {}
        if (!r) return null;
        /* username can be null — getReferrerByCode falls back for `name` but not
           for `username`, so an affiliate who never set one returns
           { name: 'A friend', username: null }. Requiring username would drop a
           perfectly valid referrer, so fall back the same way the service does. */
        var display = r.username || r.name;
        return display ? { name: r.name, username: display } : null;
      })
      .catch(function (err) {
        /* request() rejects on a non-2xx too, and its message carries the body —
           which is how "Cannot GET /shopify/proxy/referrals/referrer" surfaced. */
        try { console.warn('[quiz002] referrer lookup FAILED', { code: code, error: (err && err.message) || err }); } catch (e) {}
        return null;
      });
  }

  NS.api = {
    request: request,
    quizSignup: quizSignup,
    checkUsername: checkUsername,
    getReferrer: getReferrer,
  };

  /*
   * Start the referrer lookup here — the earliest point where BOTH the code
   * (quiz-002-data.js) and NS.api (this file) exist.
   *
   * The call is a Shopify App Proxy hop and measured 700-1600ms, and it gates the
   * funnel's first paint. Firing it now overlaps the round trip with the parse
   * and boot of quiz-002-custom.js, -custom2.js and quiz-002.js, instead of
   * queueing behind them.
   *
   * A promise, not a result: the screen may render before it settles, and
   * awaiting a settled promise costs nothing.
   */
  NS.referrerPromise = NS.wantsReferralLanding && NS.referralCode
    ? getReferrer(NS.referralCode())
    : null;
})(window.ReformlyQuiz);
